import { convertToUserTime } from "./dateUtils/convertToUserTime";
import { daysUntil, renewByInfo, expiryInfo, phaseToClass, workingDaysUntil, clampMidnight } from "./expiryUtils";

export type Status = 'submitted' | 'active' | 'expiring' | 'expired';
export const RENEWAL_LEAD_DAYS = 90;

// Returns competency status label mapping (e.g. active → "Active").
export const statusLabel: Record<Status, string> = { submitted: 'Submitted', active: 'Active', expiring: 'Expiring', expired: 'Expired' };

// Evaluates firearm status based on application/renewal/expiry dates.
// Rules:
// - If an applicationRenewalDate exists → 'submitted'
// - If isExpired is true OR licence already expired → 'expired'
// - If renew-by date has passed but licence not yet expired → 'expiring'
// - Otherwise → 'active'
export function getStatus(
    row: { applicationRenewalDate?: Date | string | null; licenceExpiryDate?: Date | string | null; isExpired?: boolean }
): Status {
    if (row.applicationRenewalDate) return 'submitted';
    const ei = expiryInfo(RENEWAL_LEAD_DAYS, row.licenceExpiryDate,);
    if (row.isExpired || (typeof ei.dToExpiry === 'number' && ei.dToExpiry < 0)) return 'expired';
    const ri = renewByInfo(RENEWAL_LEAD_DAYS, row.licenceExpiryDate);
    if (typeof ri.dToRenewBy === 'number' && ri.dToRenewBy < 0) return 'expiring';
    return 'active';
}

// Produces display-ready info about firearm licence renew-by date.
// Uses renewByInfo with RENEWAL_LEAD_DAYS (default 90 WORKING days).
// Returns:
// - formatted date text (convertToUserTime)
// - daysFromRenewBy (positive = remaining working days, negative = overdue working days)
// - Tailwind class based on phase (ok | warn | error)
// - display string like "YYYY-MM-DD ( -3 working days )"
// If no expiry provided → fallback with "n/a"
export function getRenewByInfo(
    expiry?: Date | string | null
) {
    const ri = renewByInfo(RENEWAL_LEAD_DAYS, expiry);
    if (!ri.date) return { dateText: 'n/a', className: 'text-slate-700', display: 'n/a' };
    const dateText = convertToUserTime(ri.renewBy.toISOString(), true);
    const cls = phaseToClass[ri.phase];
    if (typeof ri.dToRenewBy !== 'number') return { dateText, className: cls, display: dateText };
    const abs = Math.abs(ri.dToRenewBy);
    const sign = ri.dToRenewBy >= 0 ? '' : '-';
    const dayLabel = abs === 1 ? 'working day' : 'working days';
    return { dateText, daysFromRenewBy: ri.dToRenewBy, className: cls, display: `${dateText} ( ${sign}${abs} ${dayLabel} )` };
}

// Produces display-ready info about working days elapsed since application submission.
// Counts working days from applicationRenewalDate to today.
// Returns:
// - daysElapsed: number of working days since submission
// - Tailwind class based on threshold (green 0-90, red >90)
// - display string like "YYYY-MM-DD (45 working days)"
// If no date provided → fallback with "n/a"
export function getApplicationDaysInfo(
    applicationDate?: Date | string | null
) {
    if (!applicationDate) return { daysElapsed: 0, className: 'text-slate-700', display: 'n/a' };
    
    const appDate = clampMidnight(new Date(applicationDate as any));
    const dateText = convertToUserTime(appDate.toISOString(), true);
    const today = clampMidnight(new Date());
    const daysElapsed = workingDaysUntil(appDate, today);
    
    // Handle edge cases (undefined means date range too large or invalid)
    if (daysElapsed === undefined || daysElapsed < 0) {
        return { daysElapsed: 0, className: 'text-slate-700', display: dateText };
    }
    
    // Green for 0-89 days, red for >=90 days
    const className = daysElapsed < 90 ? 'text-green-600' : 'text-red-600';
    const dayLabel = daysElapsed === 1 ? 'working day' : 'working days';
    const display = `${dateText} ( ${daysElapsed} ${dayLabel} )`;
    
    return { daysElapsed, className, display };
}

// Re-exports daysUntil so existing imports elsewhere keep working.
export { daysUntil };
